import React, { useState, useEffect, useCallback, useRef } from "react";
import * as styles from "./ImageGallery.module.css";
import { showToast } from "../../../components/ToastComponent";

// Import our new service functions
import {
  listImages,
  uploadImages,
  renameImage,
  deleteImage,
} from "../../siteServices/ImageGalleryApis";
import Button from "../../customComponents/Button";
import Modal from "../../../components/Modal";

const ImageGallery = () => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popImage, setPopImage] = useState("");

  const handlePopImage = (image) => {
    window.open(image, "_blanck");
  };

  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listImages();
      const response = await data.json();
      // Sort by modified date, newest first
      setImages(
        (response.images || []).sort(
          (a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt)
        )
      );
    } catch (e) {
      showToast(e.message || "Failed to load images.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleFileSelect = (event) => {
    if (event.target.files) {
      setSelectedFiles(Array.from(event.target.files));
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return showToast("SELECT_FILES_TO_UPLOAD");
    setIsUploading(true);

    try {
      const result = await uploadImages(selectedFiles);
      showToast(result.message);
      setSelectedFiles([]);
      fetchImages(); // Refresh the gallery
    } catch (e) {
      showToast(e.message || "Upload failed.");
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url);
    showToast("URL_COPIED");
  };

  const handleDelete = async (url, filename) => {
    if (!window.confirm(`Delete "${filename}" forever? This cannot be undone.`))
      return;
    try {
      const result = await deleteImage(url);
      const response = await result.json();
      showToast(response.message);
      fetchImages();
    } catch (e) {
      showToast(e.message || "Delete failed.");
    }
  };

  return (
    <div>
      <h1 className={styles.galleryHeading}>Image Asset Gallery (SFTP)</h1>

      <div className={`${styles.uploadSection} blur_card`}>
        <h3 className={styles.uploadText}>Upload New Images</h3>
        <div className={styles.dropzone}>
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            accept="image/*"
            ref={inputRef}
            id="fileInput"
            style={{ display: "none" }}
          />
          <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
            Click to select files
          </label>
        </div>
        {selectedFiles.length > 0 && (
          <>
            <div className={styles.previewGrid}>
              {selectedFiles.map((file, i) => (
                <img
                  key={i}
                  src={URL.createObjectURL(file)}
                  className={styles.previewImage}
                  alt="Preview"
                />
              ))}
            </div>

            <Button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={isUploading}
              style={{ marginTop: "1.5rem" }}
              varient="light"
            >
              {isUploading
                ? "Uploading..."
                : `Upload ${selectedFiles.length} File(s)`}
            </Button>
          </>
        )}
      </div>

      <h2 className={styles.galleryHeading}>Gallery</h2>
      {isLoading ? (
        <p>Loading gallery from SFTP...</p>
      ) : (
        <div className={styles.galleryGrid}>
          {images.map((img) => (
            <div key={img.filename} className={`${styles.imageCard} blur_card`}>
              <img src={img.url} alt={img.filename} />
              <div className={styles.overlay}>
                <p
                  title={img.filename}
                  onClick={() => {
                    handlePopImage(img.url);
                  }}
                >
                  {img.filename}
                </p>
                <div className={styles.actions}>
                  <Button
                    className="btn btn-sm"
                    onClick={() => handleCopyUrl(img.url, img.filename)}
                    varient="light"
                  >
                    copy
                  </Button>

                  <Button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(img.url, img.filename)}
                    varient="danger"
                  >
                    delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
